import { Check, Info } from "lucide-react";
import { money } from "@/lib/format";

const amountText = (amount, suffix) => {
  if (amount === null || amount === undefined) return "";
  return `${money(amount)}${suffix || ""}`;
};

/**
 * "What your fee covers" — the customer-facing scope of the quote.
 *
 * Deliberately carries NO amounts against any single item: the customer sees
 * ONE figure (rendered by `QuoteTotals`) and this list tells them what that
 * figure includes. The itemised fee derivation is team-only and is stripped by
 * backend/pricing_engine.strip_internal() before anything reaches this screen.
 */
export const QuoteCovers = ({ covers = [] }) => (
  <div data-testid="quote-covers" className="space-y-4">
    {covers.map((group, groupIndex) => (
      <section
        key={`${group.title}-${groupIndex}`}
        data-testid="quote-covers-group"
        className="overflow-hidden rounded-xl border border-border bg-white"
      >
        <header className="bg-primary px-4 py-3 sm:px-5">
          <h3 className="text-[13px] font-bold uppercase tracking-[0.1em] text-white">
            {group.title}
          </h3>
          {group.subtitle ? (
            <p className="mt-0.5 text-xs text-white/75">{group.subtitle}</p>
          ) : null}
        </header>

        <ul className="divide-y divide-border">
          {(group.items || []).map((item, itemIndex) => (
            <li
              key={`${item}-${itemIndex}`}
              data-testid="quote-covers-item"
              className="flex items-start gap-3 px-4 py-3 sm:px-5"
            >
              <span
                className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[color:var(--tt-teal-mist)] text-primary"
                aria-hidden="true"
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
              </span>
              <span className="text-sm leading-snug text-foreground">{item}</span>
            </li>
          ))}
        </ul>

        {group.note ? (
          <p
            data-testid="quote-covers-note"
            className="border-t border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-4 py-3 text-xs leading-relaxed text-[color:var(--tt-teal-deep)] sm:px-5"
          >
            {group.note}
          </p>
        ) : null}
      </section>
    ))}
  </div>
);

/**
 * Generic renderer for the itemised fee derivation produced by
 * backend/pricing_engine.py. TEAM ONLY — every customer-facing surface receives
 * `sections: []`, so this renders nothing there.
 */
export const QuoteBreakdown = ({ sections = [], compact = false }) => (
  <div data-testid="quote-breakdown" className={compact ? "space-y-3" : "space-y-4"}>
    {sections.map((section, sectionIndex) => (
      <section
        key={`${section.title}-${sectionIndex}`}
        data-testid="quote-section"
        className="overflow-hidden rounded-xl border border-border bg-white"
      >
        <header className="bg-primary px-4 py-3 sm:px-5">
          <h3 className="text-[13px] font-bold uppercase tracking-[0.1em] text-white">
            {section.title}
          </h3>
          {section.subtitle ? (
            <p className="mt-0.5 text-xs text-white/75">{section.subtitle}</p>
          ) : null}
        </header>

        <div className="divide-y divide-border">
          {(section.lines || []).map((line, lineIndex) => (
            <div
              key={`${line.label}-${lineIndex}`}
              data-testid="quote-line-item"
              className="flex items-start justify-between gap-4 px-4 py-3.5 sm:px-5"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium leading-snug text-foreground">{line.label}</p>
                {line.detail ? (
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {line.detail}
                  </p>
                ) : null}
              </div>
              <p className="shrink-0 whitespace-nowrap font-amount text-sm font-bold text-foreground">
                {amountText(line.amount, line.suffix)}
              </p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-4 py-3 sm:px-5">
          <p className="text-sm font-bold text-[color:var(--tt-teal-deep)]">
            {section.subtotal_label || "Subtotal"}
          </p>
          <p
            data-testid="quote-section-subtotal"
            className="whitespace-nowrap font-amount text-sm font-bold text-[color:var(--tt-teal-deep)]"
          >
            {amountText(section.subtotal, section.subtotal_suffix)}
          </p>
        </div>
      </section>
    ))}
  </div>
);

export const QuoteTotals = ({ totals = [], animatedValue = null }) => (
  <div data-testid="quote-totals" className="space-y-2.5">
    {totals.map((total, index) => {
      const isFirstEmphasis =
        total.emphasis && totals.findIndex((t) => t.emphasis) === index;
      const displayAmount =
        isFirstEmphasis && animatedValue !== null ? animatedValue : total.amount;

      if (total.emphasis) {
        return (
          <div
            key={`${total.label}-${index}`}
            data-testid={isFirstEmphasis ? "quote-grand-total" : "quote-emphasis-total"}
            className="rounded-xl bg-primary px-5 py-5 sm:px-6"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/70">
              {total.label}
            </p>
            <p className="mt-1.5 font-amount text-[34px] font-bold leading-none text-white sm:text-[42px]">
              {money(displayAmount)}
              {total.suffix ? (
                <span className="ml-1 text-lg font-semibold text-white/85 sm:text-xl">
                  {total.suffix}
                </span>
              ) : null}
            </p>
            {total.detail ? (
              <p className="mt-2 text-xs text-white/75">{total.detail}</p>
            ) : null}
          </div>
        );
      }

      return (
        <div
          key={`${total.label}-${index}`}
          data-testid="quote-secondary-total"
          className="flex items-start justify-between gap-4 rounded-lg border border-border bg-[color:var(--tt-surface-2)] px-4 py-3"
        >
          <div>
            <p className="text-sm font-semibold text-foreground">{total.label}</p>
            {total.detail ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{total.detail}</p>
            ) : null}
          </div>
          <p className="shrink-0 whitespace-nowrap font-amount text-base font-bold text-[color:var(--tt-teal-deep)]">
            {money(total.amount)}
            {total.suffix ? (
              <span className="ml-0.5 text-xs font-semibold text-muted-foreground">
                {total.suffix}
              </span>
            ) : null}
          </p>
        </div>
      );
    })}
  </div>
);

export const QuoteDisclaimer = ({ disclaimer, notices = [] }) => (
  <div
    data-testid="quote-indicative-disclaimer"
    className="rounded-xl border p-5"
    style={{
      borderColor: "var(--tt-warn-border)",
      backgroundColor: "var(--tt-warn-bg)",
    }}
  >
    <p
      className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.08em]"
      style={{ color: "var(--tt-warn-text)" }}
    >
      <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
      Please read — this is an indicative quote
    </p>
    <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: "#4A3B10" }}>
      {disclaimer}
    </p>
    {notices.map((notice, index) => (
      <p
        key={`${notice}-${index}`}
        data-testid="quote-notice"
        className="mt-3 text-[13px] leading-relaxed"
        style={{ color: "#4A3B10" }}
      >
        {notice}
      </p>
    ))}
  </div>
);
